/**
 * Calendar Routes Configuration
 *
 * This file contains all calendar-related route definitions extracted from the main router.
 * It provides better organization and maintainability for calendar-specific routing logic.
 */

import type { NavigationGuardWithThis, RouteRecordRaw } from 'vue-router'
import constants from '@/exports/constants'
import { br } from '@/plugins/trackerPlugin'
import { loadLocales } from '@/utils/i18n.utils'
import { useCommonStore } from '@/stores/CommonStore'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'

// Calendar component imports
const CalendarLayout = () => import('@/layouts/CalendarLayout.vue')
const CalendarChildLayout = () =>
  import('@/pages/calendar/CalendarChildLayout.vue')
const CalendarMonthView = () => import('@/pages/calendar/CalendarMonthView.vue')
const CalendarDayView = () => import('@/pages/calendar/CalendarDayView.vue')
const CalendarWeekView = () => import('@/pages/calendar/CalendarWeekView.vue')
const CalendarEntryForm = () => import('@/pages/calendar/CalendarEntryForm.vue')
const CalendarGraveyard = () => import('@/pages/calendar/CalendarGraveyard.vue')
const Calendar3Column = () => import('@/pages/calendar/Calendar3Column.vue')
const CalendarSettings = () => import('@/pages/calendar/CalendarSettings.vue')

/**
 * Get calendar route configuration
 */
export function getCalendarRoutes(
  beforeEnterFn?: NavigationGuardWithThis<undefined>
): RouteRecordRaw {
  return {
    name: 'calendarLayout',
    path: 'calendar',
    alias: '',
    component: CalendarLayout,
    beforeEnter: async () => {
      await loadLocales('calendar')
    },
    props: true,
    meta: { trackingName: br.appActionFeature.calendar, section: 'calendar' },
    children: [
      {
        name: 'calendar',
        path: '/calendar',
        component: CalendarChildLayout,
        props: true,
        redirect: () => ({ name: 'calendarMonth' }),
        children: [
          {
            name: 'calendarMonth',
            path: '/calendar/month',
            component: CalendarMonthView,
            beforeEnter: beforeEnterFn,
            props: true,
            meta: {
              type: constants.viewTypes.month,
              typeName: constants.viewTypeNames.month,
              nameForLabels: 'calendar.view.emptyCta',
              addNewRouteName: 'newCalendarEntry'
            }
          },
          {
            name: 'calendarMonthView',
            path: '/calendar/month/:startDate',
            component: CalendarMonthView,
            beforeEnter: beforeEnterFn,
            props: true,
            meta: {
              type: constants.viewTypes.month,
              typeName: constants.viewTypeNames.month,
              nameForLabels: 'calendar.view.emptyCta',
              addNewRouteName: 'newCalendarEntry'
            }
          },
          {
            name: 'calendarWithDate',
            path: '/calendar/month/:startDate',
            component: CalendarMonthView,
            beforeEnter: beforeEnterFn,
            props: true,
            meta: {
              type: constants.viewTypes.month,
              typeName: constants.viewTypeNames.month,
              nameForLabels: 'calendar.view.emptyCta',
              addNewRouteName: 'newCalendarEntry'
            }
          },
          {
            name: 'calendarDayView',
            path: '/calendar/day',
            component: CalendarDayView,
            beforeEnter: beforeEnterFn,
            props: true,
            meta: {
              type: constants.viewTypes.day,
              typeName: constants.viewTypeNames.day,
              nameForLabels: 'calendar.view.emptyCta',
              addNewRouteName: 'newCalendarEntry'
            }
          },
          {
            name: 'calendarDayViewWithDate',
            path: '/calendar/day/:startDate',
            component: CalendarDayView,
            beforeEnter: beforeEnterFn,
            props: true,
            meta: {
              type: constants.viewTypes.day,
              typeName: constants.viewTypeNames.day,
              nameForLabels: 'calendar.view.emptyCta',
              addNewRouteName: 'newCalendarEntry'
            }
          },
          {
            name: 'calendarWeekView',
            path: '/calendar/week',
            component: CalendarWeekView,
            beforeEnter: beforeEnterFn,
            props: true,
            meta: {
              type: constants.viewTypes.week,
              typeName: constants.viewTypeNames.week,
              nameForLabels: 'calendar.view.emptyCta',
              addNewRouteName: 'newCalendarEntry'
            }
          },
          {
            name: 'calendarWeekViewWithDate',
            path: '/calendar/week/:startDate',
            component: CalendarWeekView,
            beforeEnter: beforeEnterFn,
            props: true,
            meta: {
              type: constants.viewTypes.week,
              typeName: constants.viewTypeNames.week,
              nameForLabels: 'calendar.view.emptyCta',
              addNewRouteName: 'newCalendarEntry'
            }
          },
          {
            name: 'calendarGraveyard',
            path: '/calendar/deleted',
            component: CalendarGraveyard,
            beforeEnter: beforeEnterFn,
            props: true,
            meta: {
              type: constants.viewTypes.graveyard,
              typeName: constants.viewTypeNames.graveyard,
              nameForLabels: 'calendar.view.emptyCta',
              addNewRouteName: 'newCalendarEntry'
            }
          },
          {
            name: 'calendarGraveyardWithDate',
            path: '/calendar/deleted/:itemId',
            component: CalendarGraveyard,
            beforeEnter: beforeEnterFn,
            props: true,
            meta: {
              type: constants.viewTypes.graveyard,
              typeName: constants.viewTypeNames.graveyard,
              nameForLabels: 'calendar.view.emptyCta',
              addNewRouteName: 'newCalendarEntry'
            }
          }
        ]
      },
      {
        name: 'calendar3Column',
        path: '/calendar',
        component: Calendar3Column,
        props: true,
        meta: {
          requiresMatched: true,
          nameForLabels: 'calendar.view.emptyCta',
          addNewRouteName: 'calendarEntry'
        }
      },
      {
        name: 'calendar3ColumnWithDate',
        path: '/calendar/:startDate',
        beforeEnter: beforeEnterFn,
        component: Calendar3Column,
        props: true,
        meta: {
          requiresMatched: true,
          nameForLabels: 'calendar.view.emptyCta',
          addNewRouteName: 'calendarEntry'
        }
      },
      {
        name: 'newCalendarEntry',
        path: '/calendar/entry',
        redirect: () => '/calendar/entry/0'
      },
      {
        name: 'calendarEntryFormNoDate',
        path: '/calendar/entry/:itemId',
        component: CalendarEntryForm,
        props: true,
        meta: { type: constants.viewTypes.form, typeName: '' }
      },
      {
        name: 'calendarEntryForm',
        path: '/calendar/entry/:itemId/:startDate',
        component: CalendarEntryForm,
        props: true,
        meta: { type: constants.viewTypes.form, typeName: '' }
      },
      {
        name: 'calendarEntryFormWithSeries',
        path: '/calendar/entry/:itemId/:startDate/:editingSeries',
        component: CalendarEntryForm,
        props: true,
        meta: { type: constants.viewTypes.form, typeName: '' }
      },
      {
        name: 'calendarSettings',
        path: '/calendar/settings',
        component: CalendarSettings,
        beforeEnter: async () => {
          const { globalFlagsLoaded, featureFlags } =
            storeToRefs(useCommonStore())

          if (!globalFlagsLoaded.value) {
            await new Promise<void>((resolve) => {
              const unwatch = watch(globalFlagsLoaded, (loaded) => {
                if (loaded) {
                  unwatch()
                  resolve()
                }
              })
            })
          }

          if (featureFlags.value?.web_calendar_connect) return true

          return { name: 'calendar' }
        },
        props: true,
        meta: { requiresAuth: true, allowRefresh: true }
      }
    ]
  }
}
